import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  CategoryScale,
} from "chart.js";

ChartJS.register(
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  CategoryScale,
);

function AppsChart({
  dataPoints = [],
  updateVideoTrack,
  osType,
  windowsSusAppData,
  macSusAppData,
}) {
  if (!dataPoints || dataPoints.length === 0)
    return (
      <div className="text-center text-gray-500 text-base pt-4">
        No application activity data found.
      </div>
    );

  const suspiciousApps =
    osType === "WINDOWS" ? windowsSusAppData : macSusAppData;

  const values = dataPoints.map((point) => {
    const suspiciousFound = point.applicationNames.some((app) =>
      suspiciousApps?.includes(app.name.toLowerCase()),
    );

    return {
      x: point.elapsedTime,
      y: point.totalApplicationsRunning,
      apps: point.applicationNames,
      suspiciousFound,
    };
  });

  const maxX = Math.max(...dataPoints.map((p) => p.elapsedTime));
  const maxY = Math.max(...dataPoints.map((p) => p.totalApplicationsRunning));

  const convertSecondsToTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const data = {
    datasets: [
      {
        label: "Applications Running",
        data: values,
        borderColor: "#206DC5",
        backgroundColor: "rgba(32,109,197,0.1)",
        borderWidth: 2,
        fill: true,
        tension: 0,

        pointRadius: 5,

        pointBackgroundColor: (context) => {
          const raw = context.raw;
          return raw.suspiciousFound ? "#ef4444" : "rgb(32,109,197)";
        },

        pointBorderColor: "#fff",
        pointBorderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,

    scales: {
      x: {
        type: "linear",
        min: 0,
        max: maxX + 10,
        title: {
          display: true,
          text: "Elapsed Time (mm:ss)",
          font: { weight: "bold" },
        },
        ticks: {
          callback: (value) => convertSecondsToTime(value),
        },
      },

      y: {
        min: 0,
        max: maxY + 2,
        title: {
          display: true,
          text: "Running Applications",
          font: { weight: "bold" },
        },
        ticks: {
          stepSize: 1,
        },
      },
    },

    plugins: {
      title: {
        display: true,
        text: "Applications Activity Timeline",
        font: {
          size: 18,
          weight: "bold",
        },
        padding: {
          top: 5,
          bottom: 7,
        },
      },

      subtitle: {
        display: true,
        text: "(Number of applications running throughout the interview)",
        font: {
          size: 13.6,
        },
        color: "#6b7280",
        padding: {
          bottom: 12,
        },
      },

      legend: {
        display: true,
        position: "top",

        onClick: (e, legendItem, legend) => {
          // Prevent click on the custom legend item
          if (legendItem.text === "Suspicious Apps Found") return;

          const index = legendItem.datasetIndex;
          const ci = legend.chart;

          if (ci.isDatasetVisible(index)) {
            ci.hide(index);
            legendItem.hidden = true;
          } else {
            ci.show(index);
            legendItem.hidden = false;
          }
        },

        labels: {
          // usePointStyle: true,
          generateLabels: (chart) => {
            const original =
              ChartJS.defaults.plugins.legend.labels.generateLabels(chart);

            return [
              ...original,
              {
                text: "Suspicious Apps Found",
                fillStyle: "#ef4444",
                strokeStyle: "#ef4444",
                hidden: false,
              },
            ];
          },
        },
      },

      tooltip: {
        displayColors: false,
        backgroundColor: "#111827",
        titleColor: "#f9fafb",
        bodyColor: "#e5e7eb",
        padding: 14,
        bodyFont: {
          size: 13,
        },
        titleFont: {
          size: 14,
          weight: "bold",
        },
        boxPadding: 6,

        callbacks: {
          title: (context) =>
            `Elapsed: ${convertSecondsToTime(context[0].parsed.x)}`,

          label: (context) => `Applications Running: ${context.parsed.y}`,

          afterBody: (context) => {
            const apps = context[0].raw.apps || [];

            if (!apps.length) return ["No applications"];

            const suspicious = suspiciousApps.map((a) => a.toLowerCase());

            return [
              ...apps.map((app) => {
                const isSus = suspicious.includes(app.name.toLowerCase());
                return isSus ? `🔴 ${app.name} (Suspicious)` : `🟢 ${app.name}`;
              }),
            ];
          },
        },
      },
    },

    interaction: {
      mode: "nearest",
      intersect: true,
    },

    onClick: (event, elements) => {
      if (elements.length > 0 && updateVideoTrack) {
        const clickedX = elements[0].element.$context.parsed.x;

        updateVideoTrack(clickedX);

        window.scrollTo({
          top: 200,
          behavior: "smooth",
        });
      }
    },

    onHover: (event, elements) => {
      event.native.target.style.cursor =
        elements.length > 0 ? "pointer" : "default";
    },
  };

  return (
    <div>
      <div className="h-[320px]">
        <Line data={data} options={options} />
      </div>
      <div className="mt-5 max-w-[88rem] mx-auto bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
        <p className="mb-[.35rem]">
          <span className="font-semibold">How to read:</span> The graph shows
          the number of applications running at each timestamp during the
          interview. Hover over any point to view the list of active
          applications at that moment. Suspicious applications detected are
          highlighted in <span className="text-red-500 font-medium">red</span>.
        </p>

        <p className="text-blue-800">
          <span className="font-semibold">Note:</span> A higher number of red
          markers or suspicious applications may indicate lower candidate
          integrity and possible use of blacklisted applications during the
          interview.
        </p>
      </div>
    </div>
  );
}

export default AppsChart;
